import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Filter, FormContainer, FormWrapper, ResetButton, SearchIcon, SearchInput, SearchLabel, ShowAllButton, StyledSearchForm } from './SearchForm.styles';
import SvgClear from '../../../../../play-core/icons/Clear';
import SvgFilter from '../../../../../play-core/icons/Filter';
import SvgSearch from '../../../../../play-core/icons/Search';
import { IconWrapper } from '../../../components/IconWrapper/IconWrapper';
import { useVideoConfig } from '../../../config/VideoAppContext';
import { useSearchDispatch, useSearchState } from '../SearchContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const SearchForm = /*#__PURE__*/forwardRef((_, ref) => {
  const dispatch = useSearchDispatch();
  const {
    searchOpen,
    searchPageOpen,
    query,
    mediaResult,
    showAll
  } = useSearchState();
  const {
    search: config
  } = useVideoConfig();
  const {
    t
  } = useTranslation();
  const formRef = useRef(null);
  const showAllVisible = config.searchShowAllSupported && query.searchTerm === '' && !showAll;
  const showFilters = searchPageOpen && config.filtersSupported && mediaResult && mediaResult.results.length > 0;
  const debouncedSearch = useDebouncedCallback(value => {
    dispatch({
      type: 'search',
      query: value
    });
  }, 300);
  const focusInput = useCallback(() => {
    const inputRef = ref;
    inputRef && inputRef.current && inputRef.current.focus();
  }, [ref]);
  const resetForm = () => formRef.current && formRef.current.reset();
  useEffect(() => {
    searchOpen ? focusInput() : resetForm();
  }, [searchOpen, focusInput]);
  const onSubmit = event => {
    event.preventDefault();
    const inputRef = ref;
    if (inputRef.current && inputRef.current.value === '') {
      if (!config.searchShowAllSupported) {
        return;
      }
      dispatch({
        type: 'show-all'
      });
    } else {
      if (inputRef.current) {
        dispatch({
          type: 'search',
          query: inputRef.current.value
        });
        dispatch({
          type: 'add-search-entry',
          searchTerm: inputRef.current.value
        });
      }
      dispatch({
        type: 'include-aggregations'
      });
    }

    // Blur the input to close the keyboard after tapping on 'Search' on iOS keyboards.
    inputRef.current?.blur();
  };
  return /*#__PURE__*/_jsxs(FormContainer, {
    children: [/*#__PURE__*/_jsxs(FormWrapper, {
      fullWidth: !showFilters,
      children: [/*#__PURE__*/_jsxs(StyledSearchForm, {
        ref: formRef,
        onSubmit: onSubmit
        // This action is required to let iOS devices show the 'Search' button in the keyboard.
        ,
        action: ".",
        children: [/*#__PURE__*/_jsx(SearchInput, {
          ref: ref,
          id: "global-search-input",
          "data-testid": "global-search-input",
          name: "search",
          type: "search",
          autoComplete: "off",
          autoCorrect: "off",
          spellCheck: false,
          placeholder: t('search.searchFieldPlaceholder'),
          defaultValue: query.searchTerm,
          isOpen: searchOpen,
          onChange: event => debouncedSearch(event.target.value),
          tabIndex: searchOpen ? 0 : -1
        }), /*#__PURE__*/_jsx(SearchLabel, {
          htmlFor: "global-search-input",
          children: t('search.searchFieldPlaceholder')
        })]
      }), showAllVisible && /*#__PURE__*/_jsx(ShowAllButton, {
        isOpen: searchOpen,
        tabIndex: searchOpen ? 0 : -1,
        onClick: () => {
          dispatch({
            type: 'show-all'
          });
          focusInput();
        },
        children: t('search.showAll')
      }), /*#__PURE__*/_jsx(ResetButton, {
        isVisible: searchOpen && query.searchTerm !== '',
        "aria-label": t('global.reset'),
        tabIndex: searchOpen ? 0 : -1,
        onClick: () => {
          dispatch({
            type: 'search',
            query: ''
          });
          resetForm();
          focusInput();
        },
        children: /*#__PURE__*/_jsx(SvgClear, {})
      })]
    }), /*#__PURE__*/_jsx(SearchIcon, {
      isOpen: searchOpen,
      children: /*#__PURE__*/_jsx(SvgSearch, {})
    }), showFilters && /*#__PURE__*/_jsx(Filter, {
      onClick: () => dispatch({
        type: 'set-mobile-filters-open',
        isOpen: true
      }),
      children: /*#__PURE__*/_jsx(IconWrapper, {
        icon: /*#__PURE__*/_jsx(SvgFilter, {})
      })
    })]
  });
});